import React, { useCallback, useEffect, useState } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import { ThemeProvider } from 'styled-components';

import ActionButton from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import { MobileSpacer } from 'components/mobile-spacer';
import PaymentMethod from 'components/payment-method';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { PaymentMethod as PaymentMethodEnum } from 'generated/graphql-gateway';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { useKeyboardState } from 'hooks/use-keyboard-state';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { SupportedCardType, defaultSupportedCardTypes } from 'state/launchdarkly/variations';
import { useLoyaltyContext } from 'state/loyalty';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { checkServiceModeUnavailable } from 'utils/restaurant';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { PlaceOrderButton } from '../place-order-button';
import SectionHeading from '../section-heading';
import { SectionContainer } from '../styled';

import DeliveryCancellationPolicy from './delivery-cancellation-policy';
import { getOrderProcessingText } from './get-order-processing-text';
import { useOrderPayment } from './order-payment/use-order-payment';
import PaymentSelection from './payment-selection';
import PrepaidMethodReload from './prepaid-method-reload';
import PrepaidTransferBalance from './prepaid-transfer-balance';
import {
  AddMoreText,
  CartPriceTooLow,
  DisclaimerCardOfferInvalid,
  DisclaimerCatering,
  DisclaimerWrapper,
  ErrorContainer,
  ErrorMessage,
  PaymentLabel,
} from './styled';
import { TableNumber } from './table-number';
import { IPaymentProps } from './types';
import { VehicleDescription } from './vehicle-description';

const Payment: React.FC<IPaymentProps> = ({
  className,
  enableOrderTimedFire,
  onPriceOrder,
  order,
  pricedAndAvailable,
  serverOrder,
  areDeliveryDetailsValid,
}) => {
  const { isKeyboardOpen, keyboardHeight } = useKeyboardState();
  const [submitting, setSubmitting] = useState(false);
  const payment = usePaymentContext();

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors();

  const {
    isCateringOrder,
    confirmStoreOnCheckout,
    openStoreConfirmationModal,
    showPaymentMethodSelection,
    showAddPaymentMethod,
    paymentMethods,
    setShowAddPaymentMethod,
    handlePaymentMethodSelected,
    onChange,
    handleAdyenInputChange,
    handleAdyenBrandDetected,
    handleAdyenInputErrors,
    paymentValues,
    errors,
    showPrepaidTransferBalance,
    showReloadPaymentMethods,
    reloadAmount,
    reloadPaymentValues,
    setReloadPaymentValues,
    needReloadAgain,
    enableImCloseDisplay,
    isOfferPaymentCardValid,
    enableDelivery,
    placeOrderAriaLabel,
    isPlacingOrder,
    isOrderPriceLoading,
    isApplePayLoading,
    showOrderProcessingDisclaimer,
    isGooglePayLoading,
    ErrorDialog,
    StoreConfirmationDialog,
    cardIsValid,
    offerIsValid,
    isValidTip,
    dismissStoreConfirmationDialog,
    isValidReloadPaymentMethodWithCard,
    setCommitOrderAfterPricing,
    focusElementWithError,
    openServiceModeUnavailableErrorDialog,
    ServiceModeUnavailableErrorDialog,
    shouldConfirmStoreOnCheckout,
    setIsHandlingOrderCommit,
    validateCreditCardForm,
    isCashPayment,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
  } = useOrderPayment({
    serverOrder,
    enableOrderTimedFire,
    defaultPaymentMethod: payment.checkoutPaymentMethodId,
  });

  const { isCatering, isDelivery, isCurbside, isTableService, serviceMode } =
    useServiceModeContext();
  const { formatCurrencyForLocale } = useUIContext();

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const checkoutCateringPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_MINIMUM);
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];
  const enableSaveCard = !useFlag(LaunchDarklyFlag.HIDE_SAVE_CARD);
  const { calculateCartTotalWithDiscount, cartEntries, cartCateringPriceTooLow } =
    useOrderContext();
  const { store } = useStoreContext();
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyUser();
  const { evaluateLoyaltyUserIncentives } = useLoyaltyContext();
  const dispatch = useAppDispatch();
  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const incentiveEvaluationErrors = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsFilteredByCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);

  const serverOrderTotal = serverOrder?.cart?.subTotalCents ?? 0;
  let cartPriceTooLow = isDelivery ? serverOrderTotal < checkoutDeliveryPriceMinimum : false;

  const initialValue = LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION);
  const [
    { value: vehicleDescription, hasError: vehicleDescriptionHasError },
    setVehicleDescription,
  ] = useState({
    value: initialValue,
    hasError: false,
  });

  const [tableNumber, setTableNumber] = useState(
    LocalStorage.getItem(StorageKeys.TABLE_NUMBER) || ''
  );

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);

  const isLoading =
    isOrderPriceLoading ||
    isPlacingOrder ||
    isApplePayLoading ||
    isGooglePayLoading ||
    evaluatingIncentives;

  const paymentErrorMessages = getErrorMessages({
    areDeliveryDetailsWithError: !areDeliveryDetailsValid,
    curbsideOrderError: isCurbside && (vehicleDescriptionHasError || !vehicleDescription),
    tableServiceError: isTableService && !tableNumber,
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError: !cardIsValid || !isOfferPaymentCardValid,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
    unableToReloadCardError: !isValidReloadPaymentMethodWithCard,
  });

  const maybeCommitOrderAfterPricing = useCallback(() => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
    if (isCurbside && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({
        customerName: `CS-${vehicleDescription}`,
      });
    }
    if (isTableService && onPriceOrder) {
      setCommitOrderAfterPricing(true);
      return onPriceOrder({
        customerName: `TABLE-${tableNumber}`,
      });
    }
    setIsHandlingOrderCommit(true);
  }, [
    dismissStoreConfirmationDialog,
    isCurbside,
    isTableService,
    onPriceOrder,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    shouldConfirmStoreOnCheckout,
    tableNumber,
    vehicleDescription,
  ]);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const handleOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (showAddPaymentMethod && !validateCreditCardForm()) {
      return;
    }

    const loyaltyId = loyaltyUser?.id;
    if (loyaltyId && showAddPaymentMethod && appliedLoyaltyOffers.length) {
      const paymentMethod = PaymentMethodEnum[paymentValues.cardType];
      dispatch(actions.loyalty.resetOfferFeedbackMap());
      await evaluateLoyaltyUserIncentives(
        loyaltyId,
        appliedLoyaltyOffers,
        cartEntries,
        appliedRewards,
        positiveCartTotal,
        paymentMethod
      );
    }

    if (!isCateringOrder && checkServiceModeUnavailable(order.serviceMode, store)) {
      openServiceModeUnavailableErrorDialog({
        message: formatMessage(
          { id: 'currentServiceModeUnavailable' },
          {
            serviceMode: getLocalizedServiceMode(order.serviceMode, formatMessage),
          }
        ),
      });
      return;
    }

    if (isCurbside) {
      if (!vehicleDescriptionHasError && vehicleDescription) {
        LocalStorage.setItem(StorageKeys.VEHICLE_DESCRIPTION, vehicleDescription);
      } else {
        return focusElementWithError('vehicleDescription');
      }
    }

    if (isTableService) {
      if (!tableNumber) {
        return focusElementWithError('tableNumber');
      }
    }

    setSubmitting(true);
  };

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected: showAddPaymentMethod,
    selectedMethodExist: !!payment.checkoutPaymentMethodId,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;
  const isPlaceOrderButtonDisabled =
    isLoading ||
    isAddNewPaymentMethodTypeSelected ||
    !!incentiveEvaluationErrors.length ||
    cartPriceTooLow;

  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];

  useEffect(() => {
    if (!submitting) {
      return;
    }
    const hasErrors = !!errorMessages.length;

    if (hasErrors) {
      setIsCheckoutErrorsModalOpen(true);
    } else if (!isDelivery && confirmStoreOnCheckout) {
      openStoreConfirmationModal();
    } else {
      maybeCommitOrderAfterPricing();
    }

    setSubmitting(false);
  }, [
    confirmStoreOnCheckout,
    errorMessages.length,
    getLoyaltyPaymentErrorMessages,
    isDelivery,
    maybeCommitOrderAfterPricing,
    openStoreConfirmationModal,
    paymentErrorMessages.length,
    submitting,
  ]);

  return (
    <ThemeProvider theme={theme}>
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          checkoutErrors={errorMessages}
          closeModal={() => {
            setIsCheckoutErrorsModalOpen(false);
          }}
        />
      )}
      <SectionContainer className={className} isCatering={isCatering} data-testid="cart-payment">
        <form onSubmit={handleOnSubmit} noValidate>
          {isCurbside && (
            <VehicleDescription
              onChange={setVehicleDescription}
              value={vehicleDescription}
              hasError={vehicleDescriptionHasError}
            />
          )}
          {isTableService && <TableNumber onChange={setTableNumber} value={tableNumber} />}
          <SectionHeading id="payment-method-header">
            {formatMessage({ id: 'paymentMethod' })}
          </SectionHeading>
          {/*TODO replace with auth.user && auth.user.fdCustomerId*/}
          <PaymentSelection>
            {showPaymentMethodSelection && (
              <PaymentLabel id="payment-method-label">
                {formatMessage({ id: 'choosePaymentMethod' })}
              </PaymentLabel>
            )}
            {showPaymentMethodSelection && (
              <PaymentMethod
                aria-expanded="true"
                aria-labelledby="payment-method-header"
                methods={paymentMethods}
                selectedMethodId={payment.checkoutPaymentMethodId}
                handleSelect={handlePaymentMethodSelected}
                isAddCardOpen={showAddPaymentMethod}
                openAddCardOnClick={setShowAddPaymentMethod}
                isAddGiftCardMethodSelected={isAddGiftCardMethodSelected}
                setIsAddGiftCardMethodSelected={setIsAddGiftCardMethodSelected}
              />
            )}
            {showAddPaymentMethod && (
              <CreditCardFormInputs
                ariaLabel="credit-card-form-inputs"
                isDelivery={isDelivery}
                onChange={onChange}
                onAdyenChange={handleAdyenInputChange}
                onAdyenBrandDetected={handleAdyenBrandDetected}
                onAdyenError={handleAdyenInputErrors}
                paymentValues={paymentValues}
                errors={errors}
                withBorder={false}
                withPadding={false}
                supportedCardTypes={supportedCardTypes}
                showSaveCard={enableSaveCard}
              />
            )}

            {/* Show PrepaidTransferBalance if the GC is selected  */}
            {showPrepaidTransferBalance && <PrepaidTransferBalance />}

            {showReloadPaymentMethods && (
              <PrepaidMethodReload
                reloadAmount={reloadAmount}
                reloadPaymentValues={reloadPaymentValues}
                setReloadPaymentValues={setReloadPaymentValues}
                isDelivery={isDelivery}
              />
            )}
            {needReloadAgain && (
              <ErrorContainer>
                <ErrorMessage>
                  {formatMessage({ id: 'insufficientBalanceReloadAgain' })}
                </ErrorMessage>
              </ErrorContainer>
            )}
            {isCateringOrder && (
              <DisclaimerCatering data-testid="disclaimer-catering">
                {`*${formatMessage({ id: 'cateringOrdersDisclaimer' })}`}
              </DisclaimerCatering>
            )}
            {showOrderProcessingDisclaimer && (
              <DisclaimerWrapper tabIndex={0}>
                {formatMessage({
                  id: getOrderProcessingText({
                    isCashPayment,
                    serviceMode: order.serviceMode,
                    enableImCloseDisplay,
                  }),
                })}
              </DisclaimerWrapper>
            )}

            {(cartPriceTooLow || cartCateringPriceTooLow) && (
              <CartPriceTooLow>
                {formatMessage(
                  {
                    id: isCateringOrder ? 'cateringMinimumNotReached' : 'deliveryMinumumNotReached',
                  },
                  {
                    min: isCateringOrder
                      ? formatCurrencyForLocale(checkoutCateringPriceMinimum)
                      : formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
                  }
                )}
                {isCateringOrder ? null : (
                  <AddMoreText>{formatMessage({ id: 'addSomethingExtra' })}</AddMoreText>
                )}
              </CartPriceTooLow>
            )}
            {!isOfferPaymentCardValid && (
              <DisclaimerCardOfferInvalid>
                {formatMessage({ id: 'invalidPaymentCardTypeWithOffer' })}
              </DisclaimerCardOfferInvalid>
            )}
            {!isDelivery || (enableDelivery && serviceMode) ? (
              <>
                <PlaceOrderButton
                  confirmation={!confirmStoreOnCheckout || isDelivery}
                  disabled={isPlaceOrderButtonDisabled}
                  isLoading={isLoading}
                  placeOrderAriaLabel={placeOrderAriaLabel}
                />
                {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
              </>
            ) : (
              <Box margin="1rem 0 0">
                <ActionButton disabled>delivery coming soon</ActionButton>
              </Box>
            )}
          </PaymentSelection>
        </form>
        <ErrorDialog />
        <ServiceModeUnavailableErrorDialog data-testid="servicemode-unavailable-error-modal" />
        <StoreConfirmationDialog onConfirm={maybeCommitOrderAfterPricing} />
      </SectionContainer>
      {isKeyboardOpen && <MobileSpacer height={keyboardHeight} />}
    </ThemeProvider>
  );
};

export default Payment;
